import {
  canonicalizeEdge,
  edgeKey,
  getVertexPoint,
  isColinearVertex,
  NEAR_ZERO_LENGTH_EPSILON,
  projectPointOntoSegment,
} from "./gridMeshGeometry";
import { collectRowDividerEdges } from "./gridRowSplit";
import type {
  GridCell,
  GridCellSeed,
  GridEdgeRef,
  GridMesh,
  GridVertex,
} from "./gridMeshTypes";
import { moveEdgeChain } from "./gridMeshTransform";
import { getGridPointIndex, gridCellSeedKey } from "./gridUtils";
import { distanceSq, type BoundaryPoint } from "./geo";

let vertexIdCounter = 0;

/** Next id for a vertex created while editing a mesh. */
const nextVertexId = (): string => {
  vertexIdCounter += 1;
  return `gv-${vertexIdCounter}`;
};

/** Reset the mesh vertex id counter so tests can assert stable ids. */
export const resetGridVertexIdCounterForTests = (): void => {
  vertexIdCounter = 0;
};

/** Turn a rectangular lattice of points into a shared-vertex mesh of cells. */
export const commitLatticeToMesh = (
  rows: number,
  columns: number,
  points: BoundaryPoint[],
  options?: {
    cellSeeds?: Record<string, GridCellSeed>;
  },
): GridMesh => {
  const vertices: Record<string, GridVertex> = {};
  const vertexIdAt = (row: number, column: number): string => {
    const id = `gv-r${row}-c${column}`;
    if (vertices[id] == null) {
      const point = points[getGridPointIndex(row, column, columns)];
      if (point == null) {
        throw new Error(`Missing lattice point at row=${row} column=${column}`);
      }
      vertices[id] = {
        id,
        latitude: point.latitude,
        longitude: point.longitude,
      };
    }
    return id;
  };

  const cells: GridCell[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const seed = options?.cellSeeds?.[gridCellSeedKey(row, column)];
      if (seed?.plotGeoId == null) {
        continue;
      }

      // SW → SE → NE → NW (row increases north in the lattice).
      const id = `gc-r${row}-c${column}`;
      const seedLabel = seed.label?.trim() ?? "";
      cells.push({
        id,
        externalPlotUuid: seed.externalPlotUuid,
        label: seedLabel,
        ...(seed.maxRow != null ? { maxRow: seed.maxRow } : {}),
        ...(seed.minRow != null ? { minRow: seed.minRow } : {}),
        plotGeoId: seed.plotGeoId,
        range: seed.range ?? rows - row,
        varietyId: seed.varietyId,
        vertexIds: [
          vertexIdAt(row, column),
          vertexIdAt(row, column + 1),
          vertexIdAt(row + 1, column + 1),
          vertexIdAt(row + 1, column),
        ],
      });
    }
  }

  const usedVertexIds = new Set<string>();
  cells.forEach((cell) => {
    cell.vertexIds.forEach((vertexId) => {
      usedVertexIds.add(vertexId);
    });
  });

  const prunedVertices: Record<string, GridVertex> = {};
  usedVertexIds.forEach((vertexId) => {
    const vertex = vertices[vertexId];
    if (vertex != null) {
      prunedVertices[vertexId] = vertex;
    }
  });

  return { cells, vertices: prunedVertices };
};

/** True when two cells share at least one undirected edge. */
const cellsShareEdge = (a: GridCell, b: GridCell): boolean => {
  const aEdges = new Set<string>();
  for (let index = 0; index < a.vertexIds.length; index += 1) {
    const start = a.vertexIds[index];
    const end = a.vertexIds[(index + 1) % a.vertexIds.length];
    if (start != null && end != null) {
      aEdges.add(edgeKey(start, end));
    }
  }

  for (let index = 0; index < b.vertexIds.length; index += 1) {
    const start = b.vertexIds[index];
    const end = b.vertexIds[(index + 1) % b.vertexIds.length];
    if (start != null && end != null && aEdges.has(edgeKey(start, end))) {
      return true;
    }
  }

  return false;
};

/**
 * True when the selection can be merged: at least two known cells that form one
 * edge-connected component (no disjoint islands).
 */
export const isMergeable = (mesh: GridMesh, selectedIds: string[]): boolean => {
  if (selectedIds.length < 2) {
    return false;
  }

  const selected = new Set(selectedIds);
  const selectedCells = mesh.cells.filter((cell) => selected.has(cell.id));
  if (selectedCells.length !== selectedIds.length) {
    return false;
  }

  // BFS over cells that share an edge; mergeable iff every selected cell is reached.
  const visited = new Set<string>();
  const queue = [selectedCells[0]?.id].filter((id): id is string => id != null);
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (currentId == null || visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    const current = selectedCells.find((cell) => cell.id === currentId);
    if (current == null) {
      continue;
    }
    selectedCells.forEach((other) => {
      if (!visited.has(other.id) && cellsShareEdge(current, other)) {
        queue.push(other.id);
      }
    });
  }

  return visited.size === selectedCells.length;
};

/** Edge-connected component that contains `startCellId`. */
const contiguousCellIdsFrom = (
  mesh: GridMesh,
  startCellId: string,
): string[] => {
  const byId = new Map(mesh.cells.map((cell) => [cell.id, cell]));
  if (!byId.has(startCellId)) {
    return [];
  }

  const visited = new Set<string>();
  const queue = [startCellId];
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (currentId == null || visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    const current = byId.get(currentId);
    if (current == null) {
      continue;
    }
    mesh.cells.forEach((other) => {
      if (!visited.has(other.id) && cellsShareEdge(current, other)) {
        queue.push(other.id);
      }
    });
  }

  return [...visited];
};

/** True when `vertexId` is a non-colinear corner of the cells' outer ring. */
const isOuterCornerOfCells = (
  mesh: GridMesh,
  vertexId: string,
  cellIds: readonly string[],
): boolean => {
  const vertex = mesh.vertices[vertexId];
  if (vertex == null || vertex.onEdge != null) {
    return false;
  }

  const selected = new Set(cellIds);
  const ring = buildOuterRingFromCells(
    mesh.cells.filter((cell) => selected.has(cell.id)),
  );
  if (ring == null) {
    return false;
  }

  const index = ring.indexOf(vertexId);
  if (index < 0) {
    return false;
  }

  const count = ring.length;
  const prevId = ring[(index - 1 + count) % count];
  const nextId = ring[(index + 1) % count];
  const prev = prevId == null ? null : mesh.vertices[prevId];
  const next = nextId == null ? null : mesh.vertices[nextId];
  if (prev == null || next == null) {
    return false;
  }

  return !isColinearVertex(prev, vertex, next);
};

/**
 * Cells to rotate from `vertexId`, when it is an outer corner of exactly one
 * edge-connected component. Edge midpoints, interior vertices, and row
 * dividers return null.
 */
export const rotationCellsForCornerVertex = (
  mesh: GridMesh,
  vertexId: string,
): string[] | null => {
  const vertex = mesh.vertices[vertexId];
  if (vertex == null || vertex.onEdge != null) {
    return null;
  }

  const seen = new Set<string>();
  const matches: string[][] = [];
  mesh.cells.forEach((cell) => {
    if (!cell.vertexIds.includes(vertexId) || seen.has(cell.id)) {
      return;
    }
    const component = contiguousCellIdsFrom(mesh, cell.id);
    component.forEach((cellId) => seen.add(cellId));
    if (isOuterCornerOfCells(mesh, vertexId, component)) {
      matches.push(component);
    }
  });

  if (matches.length !== 1) {
    return null;
  }

  return matches[0] ?? null;
};

/**
 * Build the outer vertex ring of a connected cell group by dropping shared
 * (internal) edges and walking the remaining boundary.
 */
const buildOuterRingFromCells = (cells: GridCell[]): string[] | null => {
  // 1) Count each undirected edge across the selection.
  //    Internal edges appear twice (once per adjacent cell); outer edges once.
  const edgeCounts = new Map<string, { count: number; edge: GridEdgeRef }>();

  cells.forEach((cell) => {
    for (let index = 0; index < cell.vertexIds.length; index += 1) {
      const startVertexId = cell.vertexIds[index];
      const endVertexId = cell.vertexIds[(index + 1) % cell.vertexIds.length];
      if (startVertexId == null || endVertexId == null) {
        continue;
      }
      const key = edgeKey(startVertexId, endVertexId);
      const existing = edgeCounts.get(key);
      if (existing == null) {
        edgeCounts.set(key, {
          count: 1,
          edge: { endVertexId, startVertexId },
        });
      } else {
        existing.count += 1;
      }
    }
  });

  // 2) Keep only outer edges (count === 1).
  const boundaryEdges = [...edgeCounts.values()]
    .filter((entry) => entry.count === 1)
    .map((entry) => entry.edge);

  if (boundaryEdges.length < 3) {
    return null;
  }

  // 3) Build an undirected adjacency list so we can walk those edges as a path.
  const adjacency = new Map<string, string[]>();
  boundaryEdges.forEach((edge) => {
    const startList = adjacency.get(edge.startVertexId) ?? [];
    startList.push(edge.endVertexId);
    adjacency.set(edge.startVertexId, startList);
    const endList = adjacency.get(edge.endVertexId) ?? [];
    endList.push(edge.startVertexId);
    adjacency.set(edge.endVertexId, endList);
  });

  // 4) Walk from an arbitrary boundary vertex: at each step take the neighbor
  //    that isn't where we just came from, until we close the loop.
  const startId = boundaryEdges[0]?.startVertexId;
  if (startId == null) {
    return null;
  }

  const ring: string[] = [startId];
  let previous: string | null = null;
  let current = startId;

  while (ring.length <= boundaryEdges.length) {
    const neighbors = adjacency.get(current) ?? [];
    const next = neighbors.find((candidate) => candidate !== previous);
    if (next == null) {
      return null;
    }
    if (next === startId) {
      break;
    }
    ring.push(next);
    previous = current;
    current = next;
  }

  return ring.length >= 3 ? ring : null;
};

/**
 * Drop ring vertices that sit on a straight run between their neighbors, unless
 * another cell still needs them as a real corner / T-junction (not merely as a
 * colinear mid on that other cell too).
 */
const removeColinearRingVertices = (
  ring: string[],
  vertices: Record<string, GridVertex>,
  protectedVertexIds: Set<string>,
): string[] => {
  if (ring.length <= 3) {
    return ring;
  }

  // Repeat until a pass removes nothing: dropping one vertex can expose new
  // colinear triples on the next iteration.
  let simplified = [...ring];
  let changed = true;

  while (changed && simplified.length > 3) {
    changed = false;
    const nextRing: string[] = [];
    const count = simplified.length;

    for (let index = 0; index < count; index += 1) {
      const prevId = simplified[(index - 1 + count) % count];
      const midId = simplified[index];
      const nextId = simplified[(index + 1) % count];
      if (prevId == null || midId == null || nextId == null) {
        continue;
      }

      const prev = getVertexPoint(vertices, prevId);
      const mid = getVertexPoint(vertices, midId);
      const next = getVertexPoint(vertices, nextId);
      if (
        prev != null &&
        mid != null &&
        next != null &&
        !protectedVertexIds.has(midId) &&
        isColinearVertex(prev, mid, next)
      ) {
        // Mid sits on prev→next and nothing else needs it — skip it.
        changed = true;
        continue;
      }

      nextRing.push(midId);
    }

    if (nextRing.length < 3) {
      break;
    }
    simplified = nextRing;
  }

  return simplified;
};

/** Drop unused vertex records after ring edits. Keeps row rings when present. */
const pruneUnusedVertices = (mesh: GridMesh): GridMesh => {
  const liveRows = (mesh.rows ?? []).filter((row) =>
    mesh.cells.some((cell) => cell.id === row.plotCellId),
  );
  const usedVertexIds = new Set<string>();
  mesh.cells.forEach((cell) => {
    cell.vertexIds.forEach((vertexId) => usedVertexIds.add(vertexId));
  });
  liveRows.forEach((row) => {
    row.vertexIds.forEach((vertexId) => usedVertexIds.add(vertexId));
  });

  const vertices: Record<string, GridVertex> = {};
  Object.entries(mesh.vertices).forEach(([id, vertex]) => {
    if (usedVertexIds.has(id)) {
      vertices[id] = vertex;
    }
  });

  return mesh.rows == null
    ? { cells: mesh.cells, vertices }
    : { cells: mesh.cells, rows: liveRows, vertices };
};

/**
 * True when `vertexId` is a removable colinear mid on `cell`'s ring (not a
 * corner, and the ring is large enough that dropping it won't collapse the face).
 */
const isRemovableColinearMidOnCell = (
  cell: GridCell,
  vertexId: string,
  vertices: Record<string, GridVertex>,
): boolean => {
  const { vertexIds } = cell;
  const count = vertexIds.length;
  if (count <= 3) {
    return false;
  }

  const index = vertexIds.indexOf(vertexId);
  if (index < 0) {
    return false;
  }

  const prevId = vertexIds[(index - 1 + count) % count];
  const nextId = vertexIds[(index + 1) % count];
  if (prevId == null || nextId == null) {
    return false;
  }

  const prev = getVertexPoint(vertices, prevId);
  const mid = getVertexPoint(vertices, vertexId);
  const next = getVertexPoint(vertices, nextId);
  if (prev == null || mid == null || next == null) {
    return false;
  }

  return isColinearVertex(prev, mid, next);
};

/**
 * Remove colinear mid-edge vertices from the given cells' rings when no other
 * cell still needs them as a non-colinear corner.
 */
const simplifyColinearOnCells = (
  mesh: GridMesh,
  cellIds: string[],
): GridMesh => {
  const selected = new Set(cellIds);
  if (selected.size === 0) {
    return mesh;
  }

  const dividerVertexIds = new Set<string>();
  collectRowDividerEdges(mesh).forEach((edge) => {
    dividerVertexIds.add(edge.startVertexId);
    dividerVertexIds.add(edge.endVertexId);
  });

  const cells = mesh.cells.map((cell) => {
    if (!selected.has(cell.id)) {
      return cell;
    }

    // Protect vertices another cell still needs as a real corner / T-junction.
    // Mutual colinear mids (straight shared boundary) are not protected, so a
    // later merge can drop the old shared mid from every ring that no longer
    // bends there. Row-divider endpoints stay even though they are colinear
    // on the plot ring.
    const protectedVertexIds = new Set<string>(dividerVertexIds);
    mesh.cells.forEach((other) => {
      if (other.id === cell.id) {
        return;
      }
      other.vertexIds.forEach((vertexId) => {
        if (!isRemovableColinearMidOnCell(other, vertexId, mesh.vertices)) {
          protectedVertexIds.add(vertexId);
        }
      });
    });

    return {
      ...cell,
      vertexIds: removeColinearRingVertices(
        cell.vertexIds,
        mesh.vertices,
        protectedVertexIds,
      ),
    };
  });

  return pruneUnusedVertices({
    cells,
    ...(mesh.rows == null ? {} : { rows: mesh.rows }),
    vertices: mesh.vertices,
  });
};

/**
 * Replace a connected selection with one cell whose ring is the selection's
 * outer boundary (internal edges removed, colinear mids simplified).
 */
export const mergeCells = (
  mesh: GridMesh,
  selectedIds: string[],
): GridMesh | null => {
  if (!isMergeable(mesh, selectedIds)) {
    return null;
  }

  const selected = new Set(selectedIds);
  const selectedCells = mesh.cells.filter((cell) => selected.has(cell.id));

  // Outer hull of the selection as a vertex id ring.
  const ring = buildOuterRingFromCells(selectedCells);
  if (ring == null) {
    return null;
  }

  const id = `gc-merged-${selectedIds.slice().sort().join("-")}`;
  const mergedCell: GridCell = {
    id,
    label: id,
    vertexIds: ring,
  };

  // Drop the selected cells; keep everyone else + the new merged cell.
  const remainingCells = [
    ...mesh.cells.filter((cell) => !selected.has(cell.id)),
    mergedCell,
  ];

  // Simplify the new ring and re-simplify neighbors: a mid that was protected
  // during an earlier merge can become mutually colinear once both sides merge.
  const remainingRows = mesh.rows?.filter(
    (row) => !selected.has(row.plotCellId),
  );
  return simplifyColinearOnCells(
    {
      cells: remainingCells,
      ...(remainingRows == null ? {} : { rows: remainingRows }),
      vertices: mesh.vertices,
    },
    remainingCells.map((cell) => cell.id),
  );
};

/** Vertex ids used by rows of the given plot cells. */
const dividerVertexIdsForCells = (
  rows: GridMesh["rows"],
  cellIds: ReadonlySet<string>,
): Set<string> => {
  const vertexIds = new Set<string>();
  rows?.forEach((row) => {
    if (!cellIds.has(row.plotCellId)) {
      return;
    }
    row.vertexIds.forEach((vertexId) => vertexIds.add(vertexId));
  });
  return vertexIds;
};

/**
 * Clone vertices shared with unselected cells so the selection can move without
 * dragging the rest of the mesh (cut an "island" free along the shared boundary).
 */
export const detachIsland = (
  mesh: GridMesh,
  selectedIds: string[],
): GridMesh => {
  const selected = new Set(selectedIds);
  if (selected.size === 0) {
    return mesh;
  }

  // 1) Count how often each vertex appears inside vs outside the selection.
  const usage = new Map<string, { inside: number; outside: number }>();
  mesh.cells.forEach((cell) => {
    const isInside = selected.has(cell.id);
    cell.vertexIds.forEach((vertexId) => {
      const entry = usage.get(vertexId) ?? { inside: 0, outside: 0 };
      if (isInside) {
        entry.inside += 1;
      } else {
        entry.outside += 1;
      }
      usage.set(vertexId, entry);
    });
  });

  // 2) For boundary-shared vertices, create a clone at the same coordinates.
  const cloneMap = new Map<string, string>();
  const vertices: Record<string, GridVertex> = { ...mesh.vertices };

  usage.forEach((counts, vertexId) => {
    if (counts.inside > 0 && counts.outside > 0) {
      const original = mesh.vertices[vertexId];
      if (original == null) {
        return;
      }
      const clonedId = nextVertexId();
      cloneMap.set(vertexId, clonedId);
      vertices[clonedId] = {
        id: clonedId,
        latitude: original.latitude,
        longitude: original.longitude,
      };
    }
  });

  // 3) Rewire selected cells to the clones; unselected cells keep the originals.
  const cells =
    cloneMap.size === 0
      ? mesh.cells
      : mesh.cells.map((cell) => {
          if (!selected.has(cell.id)) {
            return cell;
          }

          return {
            ...cell,
            vertexIds: cell.vertexIds.map(
              (vertexId) => cloneMap.get(vertexId) ?? vertexId,
            ),
          };
        });

  // Row dividers stay off the plot ring. Point them at this cell's cloned
  // corners so they follow the island instead of the neighbor left behind.
  retargetDividerEdges(
    vertices,
    cloneMap,
    dividerVertexIdsForCells(mesh.rows, selected),
  );

  // 4) After the cut, former shared mid-edge vertices can become colinear on both
  //    the island and the remnant mesh; clean both sides.
  const allCellIds = cells.map((cell) => cell.id);
  const rows = mesh.rows?.map((row) => {
    if (!selected.has(row.plotCellId)) {
      return row;
    }
    return {
      ...row,
      vertexIds: row.vertexIds.map(
        (vertexId) => cloneMap.get(vertexId) ?? vertexId,
      ),
    };
  });
  return simplifyColinearOnCells(
    {
      cells,
      ...(rows == null ? {} : { rows }),
      vertices,
    },
    allCellIds,
  );
};

/** Point row dividers at replacement corner ids after a clone or merge. */
const retargetDividerEdges = (
  vertices: Record<string, GridVertex>,
  remap: ReadonlyMap<string, string>,
  dividerVertexIds?: ReadonlySet<string>,
): void => {
  if (remap.size === 0) {
    return;
  }

  Object.values(vertices).forEach((vertex) => {
    if (vertex.onEdge == null) {
      return;
    }
    if (dividerVertexIds != null && !dividerVertexIds.has(vertex.id)) {
      return;
    }
    const startVertexId =
      remap.get(vertex.onEdge.startVertexId) ?? vertex.onEdge.startVertexId;
    const endVertexId =
      remap.get(vertex.onEdge.endVertexId) ?? vertex.onEdge.endVertexId;
    if (
      startVertexId === vertex.onEdge.startVertexId &&
      endVertexId === vertex.onEdge.endVertexId
    ) {
      return;
    }
    vertices[vertex.id] = {
      ...vertex,
      onEdge: {
        ...vertex.onEdge,
        endVertexId,
        startVertexId,
      },
    };
  });
};

/** True when the cell ring contains this undirected edge. */
const cellContainsEdge = (cell: GridCell, edge: GridEdgeRef): boolean => {
  const key = edgeKey(edge.startVertexId, edge.endVertexId);
  for (let index = 0; index < cell.vertexIds.length; index += 1) {
    const startVertexId = cell.vertexIds[index];
    const endVertexId = cell.vertexIds[(index + 1) % cell.vertexIds.length];
    if (
      startVertexId != null &&
      endVertexId != null &&
      edgeKey(startVertexId, endVertexId) === key
    ) {
      return true;
    }
  }
  return false;
};

/** Cell edges whose endpoints are both in the chain, one entry per undirected edge. */
const collectUndirectedChainEdges = (
  mesh: GridMesh,
  chainVertexIds: string[],
): GridEdgeRef[] => {
  const chainSet = new Set(chainVertexIds);
  const unique = new Map<string, GridEdgeRef>();
  mesh.cells.forEach((cell) => {
    for (let index = 0; index < cell.vertexIds.length; index += 1) {
      const startVertexId = cell.vertexIds[index];
      const endVertexId = cell.vertexIds[(index + 1) % cell.vertexIds.length];
      if (
        startVertexId == null ||
        endVertexId == null ||
        !chainSet.has(startVertexId) ||
        !chainSet.has(endVertexId)
      ) {
        continue;
      }
      const edge = canonicalizeEdge(startVertexId, endVertexId);
      unique.set(edgeKey(edge.startVertexId, edge.endVertexId), edge);
    }
  });
  return [...unique.values()];
};

/** Chain vertices that belong to only one chain edge. */
const findChainEndVertexIds = (chainEdges: GridEdgeRef[]): string[] => {
  const degree = new Map<string, number>();
  chainEdges.forEach((edge) => {
    degree.set(edge.startVertexId, (degree.get(edge.startVertexId) ?? 0) + 1);
    degree.set(edge.endVertexId, (degree.get(edge.endVertexId) ?? 0) + 1);
  });
  return [...degree.entries()]
    .filter(([, count]) => count === 1)
    .map(([vertexId]) => vertexId);
};

/**
 * Clone only the terminal vertices of a colinear chain, and rewire cells that
 * own a chain-end edge onto those clones. Intermediate chain vertices stay
 * shared; adjacent cells that only touch an end keep the originals.
 */
export const detachChainEndVertices = (
  mesh: GridMesh,
  chainVertexIds: string[],
): { mesh: GridMesh; vertexIds: string[] } => {
  const chainEdges = collectUndirectedChainEdges(mesh, chainVertexIds);
  const endIds = findChainEndVertexIds(chainEdges);
  if (endIds.length === 0) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const vertices: Record<string, GridVertex> = { ...mesh.vertices };
  const remap = new Map<string, string>();
  const ownerCellIds = new Set<string>();

  endIds.forEach((vertexId) => {
    const incident = chainEdges.filter(
      (edge) =>
        edge.startVertexId === vertexId || edge.endVertexId === vertexId,
    );
    const owners = new Set(
      mesh.cells
        .filter((cell) => incident.some((edge) => cellContainsEdge(cell, edge)))
        .map((cell) => cell.id),
    );
    const needsClone = mesh.cells.some(
      (cell) => !owners.has(cell.id) && cell.vertexIds.includes(vertexId),
    );
    if (!needsClone) {
      return;
    }
    const original = mesh.vertices[vertexId];
    if (original == null) {
      return;
    }
    const clonedId = nextVertexId();
    vertices[clonedId] = {
      id: clonedId,
      latitude: original.latitude,
      longitude: original.longitude,
    };
    remap.set(vertexId, clonedId);
    owners.forEach((cellId) => ownerCellIds.add(cellId));
  });

  if (remap.size === 0) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const cells = mesh.cells.map((cell) => {
    if (!ownerCellIds.has(cell.id)) {
      return cell;
    }
    return {
      ...cell,
      vertexIds: cell.vertexIds.map(
        (vertexId) => remap.get(vertexId) ?? vertexId,
      ),
    };
  });
  const rows = mesh.rows?.map((row) => {
    if (!ownerCellIds.has(row.plotCellId)) {
      return row;
    }
    return {
      ...row,
      vertexIds: row.vertexIds.map(
        (vertexId) => remap.get(vertexId) ?? vertexId,
      ),
    };
  });
  retargetDividerEdges(
    vertices,
    remap,
    dividerVertexIdsForCells(mesh.rows, ownerCellIds),
  );

  return {
    mesh: {
      cells,
      ...(rows == null ? {} : { rows }),
      vertices,
    },
    vertexIds: chainVertexIds.map(
      (vertexId) => remap.get(vertexId) ?? vertexId,
    ),
  };
};

/**
 * Clone endpoints used by cells that do not contain `edge`, and rewire every
 * cell that shares the edge onto those clones. Adjacent cells keep the
 * originals so a later slide does not pull them.
 */
export const detachEdgeVertices = (
  mesh: GridMesh,
  edge: GridEdgeRef,
): { edge: GridEdgeRef; mesh: GridMesh } => {
  const detached = detachChainEndVertices(mesh, [
    edge.startVertexId,
    edge.endVertexId,
  ]);
  return {
    edge: {
      endVertexId: detached.vertexIds[1] ?? edge.endVertexId,
      startVertexId: detached.vertexIds[0] ?? edge.startVertexId,
    },
    mesh: detached.mesh,
  };
};

/** Vertices on cells that do not own a chain edge, excluding the chain itself. */
const collectForeignVertexIdsForChain = (
  mesh: GridMesh,
  chainVertexIds: string[],
  chainEdges: GridEdgeRef[],
): string[] => {
  const chainSet = new Set(chainVertexIds);
  const cellsWithChainEdge = new Set(
    mesh.cells
      .filter((cell) => chainEdges.some((edge) => cellContainsEdge(cell, edge)))
      .map((cell) => cell.id),
  );

  const foreignIds: string[] = [];
  const seen = new Set<string>();
  mesh.cells.forEach((cell) => {
    if (cellsWithChainEdge.has(cell.id)) {
      return;
    }
    cell.vertexIds.forEach((vertexId) => {
      if (chainSet.has(vertexId) || seen.has(vertexId)) {
        return;
      }
      seen.add(vertexId);
      foreignIds.push(vertexId);
    });
  });
  return foreignIds;
};

/** Nearest chain-foreign vertex that overlaps this one within the snap epsilon. */
const findOverlappingForeignVertex = (
  mesh: GridMesh,
  vertexId: string,
  foreignIds: string[],
): { distanceSq: number; foreignVertexId: string } | null => {
  const point = getVertexPoint(mesh.vertices, vertexId);
  if (point == null) {
    return null;
  }

  let best: { distanceSq: number; foreignVertexId: string } | null = null;
  foreignIds.forEach((foreignVertexId) => {
    const foreign = getVertexPoint(mesh.vertices, foreignVertexId);
    if (foreign == null) {
      return;
    }
    const overlapDistanceSq = distanceSq(point, foreign);
    if (overlapDistanceSq > NEAR_ZERO_LENGTH_EPSILON) {
      return;
    }
    if (best == null || overlapDistanceSq < best.distanceSq) {
      best = { distanceSq: overlapDistanceSq, foreignVertexId };
    }
  });
  return best;
};

/**
 * If a slid chain-end vertex overlaps a vertex from another cell, translate
 * the whole chain onto that vertex (slope preserved) and merge coincident ids.
 */
export const mergeOverlappingChainVertices = (
  mesh: GridMesh,
  chainVertexIds: string[],
): { mesh: GridMesh; vertexIds: string[] } => {
  const chainEdges = collectUndirectedChainEdges(mesh, chainVertexIds);
  const endIds = findChainEndVertexIds(chainEdges);
  if (endIds.length === 0) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const foreignIds = collectForeignVertexIdsForChain(
    mesh,
    chainVertexIds,
    chainEdges,
  );
  if (foreignIds.length === 0) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const snap = endIds.reduce<{
    distanceSq: number;
    foreignVertexId: string;
    vertexId: string;
  } | null>((best, vertexId) => {
    const overlap = findOverlappingForeignVertex(mesh, vertexId, foreignIds);
    if (overlap == null) {
      return best;
    }
    if (best != null && overlap.distanceSq >= best.distanceSq) {
      return best;
    }
    return {
      distanceSq: overlap.distanceSq,
      foreignVertexId: overlap.foreignVertexId,
      vertexId,
    };
  }, null);
  if (snap == null) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const source = mesh.vertices[snap.vertexId];
  const target = mesh.vertices[snap.foreignVertexId];
  if (source == null || target == null) {
    return { mesh, vertexIds: chainVertexIds };
  }

  const aligned = moveEdgeChain(mesh, chainVertexIds, {
    latitude: target.latitude - source.latitude,
    longitude: target.longitude - source.longitude,
  });

  const remap = new Map<string, string>();
  endIds.forEach((vertexId) => {
    const overlap = findOverlappingForeignVertex(aligned, vertexId, foreignIds);
    if (overlap != null) {
      remap.set(vertexId, overlap.foreignVertexId);
    }
  });
  if (remap.size === 0) {
    return { mesh: aligned, vertexIds: chainVertexIds };
  }

  const cells = aligned.cells.map((cell) => ({
    ...cell,
    vertexIds: cell.vertexIds.map(
      (vertexId) => remap.get(vertexId) ?? vertexId,
    ),
  }));

  const rows = aligned.rows?.map((row) => ({
    ...row,
    vertexIds: row.vertexIds.map((vertexId) => remap.get(vertexId) ?? vertexId),
  }));
  retargetDividerEdges(aligned.vertices, remap);

  return {
    mesh: pruneUnusedVertices({
      cells,
      ...(rows == null ? {} : { rows }),
      vertices: aligned.vertices,
    }),
    vertexIds: chainVertexIds.map(
      (vertexId) => remap.get(vertexId) ?? vertexId,
    ),
  };
};

/**
 * If a slid edge endpoint overlaps a vertex from another cell, translate the
 * whole edge onto that vertex (slope preserved) and merge the coincident ids.
 */
export const mergeOverlappingEdgeVertices = (
  mesh: GridMesh,
  edge: GridEdgeRef,
): { edge: GridEdgeRef; mesh: GridMesh } => {
  const merged = mergeOverlappingChainVertices(mesh, [
    edge.startVertexId,
    edge.endVertexId,
  ]);
  return {
    edge: {
      endVertexId: merged.vertexIds[1] ?? edge.endVertexId,
      startVertexId: merged.vertexIds[0] ?? edge.startVertexId,
    },
    mesh: merged.mesh,
  };
};

/** True when any selected-cell vertex is also used by an unselected cell. */
export const selectionSharesExternalVertices = (
  mesh: GridMesh,
  selectedIds: string[],
): boolean => {
  const selected = new Set(selectedIds);
  const selectedVertexIds = new Set<string>();
  mesh.cells.forEach((cell) => {
    if (selected.has(cell.id)) {
      cell.vertexIds.forEach((vertexId) => selectedVertexIds.add(vertexId));
    }
  });

  return mesh.cells.some(
    (cell) =>
      !selected.has(cell.id) &&
      cell.vertexIds.some((vertexId) => selectedVertexIds.has(vertexId)),
  );
};

/** Remove a cell and prune vertices that nothing else references. */
export const deleteCell = (mesh: GridMesh, cellId: string): GridMesh | null => {
  if (!mesh.cells.some((cell) => cell.id === cellId)) {
    return null;
  }

  return pruneUnusedVertices({
    cells: mesh.cells.filter((cell) => cell.id !== cellId),
    ...(mesh.rows == null ? {} : { rows: mesh.rows }),
    vertices: mesh.vertices,
  });
};

/**
 * Delete an edge:
 * - Shared by two (or more) cells → merge those cells.
 * - Owned by a single cell → deleting it opens the ring, so delete that cell.
 */
export const deleteEdge = (
  mesh: GridMesh,
  edge: GridEdgeRef,
): GridMesh | null => {
  const cellsWithEdge = mesh.cells.filter((cell) =>
    cellContainsEdge(cell, edge),
  );
  if (cellsWithEdge.length === 0) {
    return null;
  }

  if (cellsWithEdge.length >= 2) {
    return mergeCells(
      mesh,
      cellsWithEdge.map((cell) => cell.id),
    );
  }

  return deleteCell(mesh, cellsWithEdge[0].id);
};

/** Insert `newVertexId` into a ring immediately after the matching undirected edge. */
const insertVertexIdBetweenEdge = (
  vertexIds: string[],
  edge: GridEdgeRef,
  newVertexId: string,
): string[] => {
  const key = edgeKey(edge.startVertexId, edge.endVertexId);
  const next = [...vertexIds];
  for (let index = 0; index < next.length; index += 1) {
    const startVertexId = next[index];
    const endVertexId = next[(index + 1) % next.length];
    if (
      startVertexId != null &&
      endVertexId != null &&
      edgeKey(startVertexId, endVertexId) === key
    ) {
      next.splice(index + 1, 0, newVertexId);
      return next;
    }
  }
  return vertexIds;
};

/**
 * Insert a shared vertex on an edge at the projection of `point` onto the
 * segment. Every cell that uses that edge gets the new vertex in its ring.
 */
export const insertVertexOnEdge = (
  mesh: GridMesh,
  edge: GridEdgeRef,
  point: BoundaryPoint,
): { mesh: GridMesh; vertexId: string } | null => {
  const start = getVertexPoint(mesh.vertices, edge.startVertexId);
  const end = getVertexPoint(mesh.vertices, edge.endVertexId);
  if (start == null || end == null) {
    return null;
  }

  if (!mesh.cells.some((cell) => cellContainsEdge(cell, edge))) {
    return null;
  }

  const projected = projectPointOntoSegment(point, start, end);
  // Reject insertions that land on an existing endpoint (would be a no-op split).
  if (
    distanceSq(projected, start) <= NEAR_ZERO_LENGTH_EPSILON ||
    distanceSq(projected, end) <= NEAR_ZERO_LENGTH_EPSILON
  ) {
    return null;
  }

  const vertexId = nextVertexId();
  const vertices: Record<string, GridVertex> = {
    ...mesh.vertices,
    [vertexId]: {
      id: vertexId,
      latitude: projected.latitude,
      longitude: projected.longitude,
    },
  };

  const cells = mesh.cells.map((cell) => {
    if (!cellContainsEdge(cell, edge)) {
      return cell;
    }
    return {
      ...cell,
      vertexIds: insertVertexIdBetweenEdge(cell.vertexIds, edge, vertexId),
    };
  });

  return {
    mesh: {
      cells,
      ...(mesh.rows == null ? {} : { rows: mesh.rows }),
      vertices,
    },
    vertexId,
  };
};

/**
 * Remove a vertex from every cell that uses it.
 * Cells that still have ≥3 vertices stay (ring closes across the gap);
 * cells that drop below 3 vertices are removed.
 */
export const deleteVertex = (
  mesh: GridMesh,
  vertexId: string,
): GridMesh | null => {
  if (mesh.vertices[vertexId] == null) {
    return null;
  }

  const affectedCellIds: string[] = [];
  const cells: GridCell[] = [];
  let touched = false;

  mesh.cells.forEach((cell) => {
    if (!cell.vertexIds.includes(vertexId)) {
      cells.push(cell);
      return;
    }

    touched = true;
    const nextVertexIds = cell.vertexIds.filter((id) => id !== vertexId);
    if (nextVertexIds.length >= 3) {
      affectedCellIds.push(cell.id);
      cells.push({
        ...cell,
        vertexIds: nextVertexIds,
      });
    }
    // else drop the cell (< 3 vertices cannot form a closed face)
  });

  if (!touched) {
    return null;
  }

  return simplifyColinearOnCells(
    pruneUnusedVertices({
      cells,
      ...(mesh.rows == null ? {} : { rows: mesh.rows }),
      vertices: mesh.vertices,
    }),
    affectedCellIds,
  );
};
